import React from 'react';
import { ShieldCheck, BarChart3, Megaphone, Settings2, Cookie, ArrowRight } from 'lucide-react';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import { Button } from '@/components/ui/button';
import { breadcrumbJsonLd } from '@/lib/jsonld';
import { useConsent, CONSENT_KEY, CONSENT_VERSION } from '@/lib/consent';

const CATEGORY_DETAIL = [
  {
    icon: ShieldCheck,
    title: 'Necessary Cookies',
    badge: 'Always on',
    body:
      'Required for core site functionality, security, and form handling — including the advertiser application form, session/state and route handling, and the admin authentication flow. These cannot be disabled.',
  },
  {
    icon: BarChart3,
    title: 'Analytics Cookies',
    badge: 'Optional',
    body:
      'Help us understand page visits, traffic sources and site performance. Examples include providers such as Google Analytics or Microsoft Clarity. Analytics scripts are only loaded if you opt in.',
  },
  {
    icon: Megaphone,
    title: 'Marketing Cookies',
    badge: 'Optional',
    body:
      'Used to measure campaign activity and support advertising-related tracking. Examples include Meta Pixel, Google Ads conversion tracking and retargeting pixels. These scripts are only loaded if you opt in.',
  },
  {
    icon: Settings2,
    title: 'Preferences Cookies',
    badge: 'Optional',
    body:
      'Remember choices such as display settings, language, and your cookie consent itself. The consent record is stored locally so you are not asked again on every visit.',
  },
];

export default function CookiePolicy() {
  const { openManager } = useConsent();
  const jsonLd = breadcrumbJsonLd([
    { label: 'Home', path: '/' },
    { label: 'Cookie Policy' },
  ]);

  return (
    <>
      <SEOHead path="/cookie-policy" jsonLd={jsonLd} />
      <PageHeader
        breadcrumbs={[{ label: 'Cookie Policy' }]}
        eyebrow="Legal"
        title="Cookie Policy"
        subtitle="How Ads Dashbound uses cookies and similar technologies on this marketing website."
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Button
            onClick={openManager}
            data-testid="cookie-policy-open-manager"
            className="btn-primary-gradient text-white rounded-xl px-5 py-2 h-10 text-[14px] font-semibold border-0"
          >
            <Cookie className="h-4 w-4 mr-1.5" /> Manage cookies
          </Button>
        </div>
      </PageHeader>

      <section className="bg-[#F5F7FB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose-dashbound">
            <h2>Overview</h2>
            <p>
              This Cookie Policy explains how Ads Dashbound uses cookies and
              similar technologies (such as <code>localStorage</code>) on this
              marketing website. It does not cover any tracking that may run
              inside the advertiser traffic panel, which is a separate product
              with its own terms.
            </p>
            <h2>What is a cookie?</h2>
            <p>
              A cookie is a small piece of data stored in your browser that lets
              a site remember information across page loads. We also use
              browser <code>localStorage</code> for the consent record itself
              and for a small number of preferences (theme, etc.).
            </p>
            <h2>Categories we use</h2>
            <p>
              You can accept all categories, reject non-essential categories, or
              customize each category individually using the cookie banner on
              first visit, the “Manage cookies” button on this page, or the
              footer link on every page.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CATEGORY_DETAIL.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="rounded-2xl bg-white border border-slate-900/10 shadow-[0_8px_24px_rgba(2,6,23,0.04)] p-5"
                >
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-[15px] font-semibold text-slate-900">
                          {c.title}
                        </h3>
                        <span className="text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-900/5 text-slate-700 border border-slate-900/10">
                          {c.badge}
                        </span>
                      </div>
                      <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 prose-dashbound">
            <h2>Where your consent is stored</h2>
            <p>
              Your consent choices are stored locally in your browser under the
              key <code>{CONSENT_KEY}</code> with version{' '}
              <code>{CONSENT_VERSION}</code>. The record contains a timestamp
              and a boolean for each category. If we update this policy in a
              way that changes which categories are used, the version will
              change and you will be asked again.
            </p>
            <h2>Changing your choices</h2>
            <p>
              You can change your cookie choices at any time using the{' '}
              <strong>“Manage cookies”</strong> link in the website footer, or
              by clicking the button below.
            </p>
            <p>
              You can also clear all consent by deleting the{' '}
              <code>{CONSENT_KEY}</code> entry from your browser storage —
              the banner will appear again on your next visit.
            </p>
            <h2>Third-party services</h2>
            <p>
              When non-essential categories are enabled, Ads Dashbound may
              load third-party scripts to collect analytics or measure
              advertising performance. These services may set their own
              cookies under their own policies. The exact list of providers
              in active use is disclosed in this section once they are
              configured. Today, no third-party tracking scripts are loaded
              from this marketing website.
            </p>
            <h2>Contact</h2>
            <p>
              If you have questions about this policy, contact{' '}
              <a
                href="mailto:contact@dashbound.media"
                className="text-[#2563EB] hover:text-[#1D4ED8] underline-offset-4 hover:underline"
              >
                contact@dashbound.media
              </a>
              .
            </p>
          </div>

          <div className="mt-10">
            <Button
              onClick={openManager}
              data-testid="cookie-policy-open-manager-bottom"
              className="btn-primary-gradient text-white rounded-xl px-5 py-2 h-10 text-[14px] font-semibold border-0"
            >
              <Cookie className="h-4 w-4 mr-1.5" /> Manage cookies
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
